/**
 * brainSlice — AgentBrain canlı snapshot store.
 *
 * Konsey Cognitive Renaissance — Bölüm D2/D4/D9.
 *
 * Backend SSE `brain.update` event'leri (`useDiscussionStream` window
 * dispatch eder) → bu slice → BrainModal canlı güncellenir.
 *
 * Yapı:
 *   • `brainSnapshots[personaId] = BrainState` — son snapshot
 *   • `brainHistory[personaId][]` — son N snapshot (UI sparkline)
 *   • `selectedBrainPersonaId` — modal'da hangi persona açık
 *
 * Throttle: backend zaten 400ms throttle ediyor (Bölüm D8); FE
 * `useThrottledBrainState` hook'u 250ms ek throttle yapar (D9).
 */
import type { StateCreator } from "zustand";
import type { DebateStore } from "../debateStore";

/** 8-lobe shape (mirror of `domain/brain.py:BrainState.model_dump()`). */
export interface BrainSnapshotShape {
  persona_id: string;
  session_id: string;
  turn_index: number;
  timestamp_ms: number;
  is_baseline: boolean;
  hippocampus: {
    recent_chunks_count: number;
    last_recall_ms: number;
    avg_recall_ms: number;
    hit_rate: number;
    queries_total: number;
  };
  cortex: {
    expertise_anchors_used: number;
    knowledge_tokens: number;
    knowledge_tokens_baseline: number;
  };
  prefrontal: {
    cognitive_load_pct: number;
    reasoning_steps: number;
    tool_calls_pending: number;
    tool_calls_total: number;
    reasoning_quality: number;
  };
  amygdala: {
    heat: number;
    polarity: number;
    threat_words_count: number;
    last_label: "pozitif" | "nötr" | "negatif" | "tehdit";
  };
  cerebellum: {
    procedural_ops_executed: number;
    last_op_latency_ms: number;
    avg_latency_ms: number;
  };
  parietal: {
    focus_target: string;
    focus_intensity: number;
  };
  temporal: {
    semantic_units_processed: number;
    comprehension_score: number;
  };
  occipital: {
    visual_inputs_count: number;
  };
}

const HISTORY_MAX = 60;

export interface BrainSlice {
  brainSnapshots: Record<string, BrainSnapshotShape>;
  brainHistory: Record<string, BrainSnapshotShape[]>;
  selectedBrainPersonaId: string | null;

  applyBrainSnapshot(snap: BrainSnapshotShape): void;
  openBrainModal(personaId: string): void;
  closeBrainModal(): void;
  resetBrain(): void;
}

export type BrainSliceCreator = StateCreator<
  DebateStore,
  [["zustand/immer", never]],
  [],
  BrainSlice
>;

export const createBrainSlice: BrainSliceCreator = (set) => ({
  brainSnapshots: {},
  brainHistory: {},
  selectedBrainPersonaId: null,

  applyBrainSnapshot: (snap) => {
    if (!snap || !snap.persona_id) return;
    set((state) => {
      state.brainSnapshots[snap.persona_id] = snap;
      const list = state.brainHistory[snap.persona_id] ?? [];
      list.push(snap);
      if (list.length > HISTORY_MAX) {
        list.splice(0, list.length - HISTORY_MAX);
      }
      state.brainHistory[snap.persona_id] = list;
    });
  },

  openBrainModal: (personaId) => {
    set((state) => {
      state.selectedBrainPersonaId = personaId;
    });
  },

  closeBrainModal: () => {
    set((state) => {
      state.selectedBrainPersonaId = null;
    });
  },

  resetBrain: () => {
    set((state) => {
      state.brainSnapshots = {};
      state.brainHistory = {};
      state.selectedBrainPersonaId = null;
    });
  },
});
